/**
 * A FELHASZNÁLÓK SZELETE — a #25 második szelete a felületen.
 *
 * A `CompanySettings` mintájára: kereső, „Inaktívak is" kapcsoló, soronkénti
 * műveletek, és a megerősítés ott, ahol a következmény NINCS a képernyőn.
 *
 * ═══ AMI SOR SZERINT ELTÉR ═══
 *
 *   • ÜGYFÉL-FELHASZNÁLÓNÁL nincs „Hozzáférés" és nincs „Meghívó": a hatóköre
 *     a cégéhez kötött identitás, a linkjét pedig a bekérő levélben kapja.
 *     Inaktiválni viszont őt is lehet — a kapcsolattartó is távozhat.
 *   • „MEGHÍVÓ ÚJRAKÜLDÉSE" csak annál látszik, aki még SOHA nem lépett be
 *     (`lastLoginAt === null`). A beváltás belépést is rögzít, tehát ez a
 *     jel pontos; ha mégsem, a szerver 409-et ad, és a mondata látszik.
 *
 * ⚠️ A SAJÁT FIÓK INAKTIVÁLÁSA NINCS ELREJTVE. A felületnek nincs megbízható
 * tudása arról, melyik sor „én" (a `/auth/me` nem ad azonosítót), és egy név
 * szerinti egyezés két azonos nevű kollégánál hazudna. A szerver 403-mal
 * utasítja el, és a mondata látszik — ez pontosabb, mint egy találgatott rejtés.
 */

import { type ReactElement, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from './ConfirmDialog';
import { UserForm } from './UserForm';
import type { CompanyRow } from './useCompanies';
import { type LayerRegistrar, useSettingsLayer } from './useSettingsLayer';
import { type UserRow, type UsersState, useUsers } from './useUsers';

type Translate = ReturnType<typeof useTranslation>['t'];

type Editing = { kind: 'new' } | { kind: 'access'; user: UserRow } | null;

/**
 * Az állapotváltás DÖNTÉS, nem adatbevitel — megerősítést kér. Kettő van:
 * az inaktiválás és az ügyfélként belépés joga (#76).
 */
type Confirming =
  | { kind: 'active'; user: UserRow; active: boolean }
  | { kind: 'impersonation'; user: UserRow; canImpersonate: boolean }
  | null;

/** A látható sorok — az inaktív-kapcsoló és a kereső (név vagy e-mail) együtt. */
export function visibleUsers(users: UserRow[], query: string, showInactive: boolean): UserRow[] {
  const needle = query.trim().toLowerCase();
  return users.filter(
    (user) =>
      (user.active || showInactive) &&
      (needle === '' ||
        user.name.toLowerCase().includes(needle) ||
        user.email.toLowerCase().includes(needle))
  );
}

export function UserSettings({ onLayer }: { onLayer: LayerRegistrar }) {
  const { t } = useTranslation();
  const state = useUsers();
  const [query, setQuery] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<Editing>(null);
  const [confirming, setConfirming] = useState<Confirming>(null);

  const visible = useMemo(
    () => visibleUsers(state.users, query, showInactive),
    [state.users, query, showInactive]
  );

  /** Mentés után, „Mégse"-re és `Escape`-re is — `CompanySettings` `closeForm`. */
  const closeForm = () => {
    state.clearWriteError();
    setEditing(null);
  };

  const blocked = blockingView(state, t);

  useSettingsLayer(onLayer, {
    hidden: blocked !== null,
    confirming: confirming !== null,
    editing: editing !== null,
    saving: state.status === 'saving',
    closeConfirm: () => setConfirming(null),
    closeForm,
  });

  if (blocked) {
    return blocked;
  }

  if (editing) {
    return (
      <UserForm
        user={editing.kind === 'access' ? editing.user : null}
        state={state}
        onDone={closeForm}
      />
    );
  }

  const inactiveCount = state.users.filter((user) => !user.active).length;

  return (
    <>
      <div className="settings__toolbar">
        <input
          type="search"
          aria-label={t('users.searchPlaceholder')}
          placeholder={t('users.searchPlaceholder')}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <label className="settings__toggle">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(event) => setShowInactive(event.target.checked)}
          />
          {t('users.showInactive')}
        </label>
        <span className="spacer" />
        <button
          type="button"
          className="settings__primary"
          onClick={() => setEditing({ kind: 'new' })}
        >
          {t('users.new')}
        </button>
      </div>

      <InvitationNoticeView state={state} />
      <WriteError state={state} />

      {visible.length === 0 ? (
        <p className="empty">{t('users.noMatch')}</p>
      ) : (
        <table className="list">
          <thead>
            <tr>
              <th>{t('users.column.name')}</th>
              <th>{t('users.column.role')}</th>
              <th>{t('users.column.scope')}</th>
              <th>{t('users.column.state')}</th>
              <th>{/* műveletek — fejléc nélkül, nem adat */}</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((user) => (
              <UserRowView
                key={user.id}
                user={user}
                companies={state.companies}
                onAccess={() => setEditing({ kind: 'access', user })}
                onResend={() => state.resendInvitation(user)}
                onToggle={() => setConfirming({ kind: 'active', user, active: !user.active })}
                onImpersonation={() =>
                  setConfirming({
                    kind: 'impersonation',
                    user,
                    canImpersonate: !user.canImpersonate,
                  })
                }
              />
            ))}
          </tbody>
        </table>
      )}

      <p className="hint">
        {t('users.count', { count: state.users.length })}
        {inactiveCount > 0
          ? ` — ${t('users.countSplit', {
              active: state.users.length - inactiveCount,
              inactive: inactiveCount,
            })}`
          : ''}
        {' · '}
        {t('users.inactiveNote')}
      </p>

      {confirming ? (
        /* A szöveg a KÖVETKEZMÉNYT mondja el, nem azt, hogy „biztos vagy
           benne": az inaktivált kolléga élő meghívó-linkje is érvénytelenné
           válik, és ez nem látszik a gombon. */
        <ConfirmDialog
          message={confirmMessage(t, confirming)}
          yesLabel={t('users.confirmYes')}
          noLabel={t('users.confirmNo')}
          onYes={() => {
            if (confirming.kind === 'active') {
              state.setActive(confirming.user.id, confirming.active);
            } else {
              state.setImpersonation(confirming.user.id, confirming.canImpersonate);
            }
            setConfirming(null);
          }}
          onNo={() => setConfirming(null)}
        />
      ) : null}
    </>
  );
}

function UserRowView({
  user,
  companies,
  onAccess,
  onResend,
  onToggle,
  onImpersonation,
}: {
  user: UserRow;
  companies: CompanyRow[];
  onAccess: () => void;
  onResend: () => void;
  onToggle: () => void;
  onImpersonation: () => void;
}) {
  const { t } = useTranslation();
  const office = user.role !== 'CLIENT';

  return (
    /* ⚠️ AZ INAKTÍV SOR NEM HALVÁNYUL EL — visszahúzott háttér, teljes
       erősségű szöveg (`design-tokens.md`: az opacity nem lefokozó csatorna). */
    <tr className={user.active ? 'row' : 'row row--inactive'}>
      <td>
        {user.name}
        <span className="row__note">{user.email}</span>
      </td>
      <td>{roleLabel(t, user)}</td>
      <td>{scopeLabel(t, user, companies)}</td>
      <td>{stateLabel(t, user)}</td>
      <td className="row__actions">
        <span className="row__buttons">
          {office ? (
            <button type="button" onClick={onAccess}>
              {t('users.access')}
            </button>
          ) : null}
          {office && user.active && user.lastLoginAt === null ? (
            <button type="button" onClick={onResend}>
              {t('users.resend')}
            </button>
          ) : null}
          {/* CSAK KÖNYVELŐNÉL (#76): az ADMIN mindig beléphet ügyfélként, az
              ügyfél soha — náluk nincs mit kapcsolni, és a szerver 400-at adna. */}
          {user.role === 'ACCOUNTANT' && user.active ? (
            <button type="button" aria-pressed={user.canImpersonate} onClick={onImpersonation}>
              {user.canImpersonate
                ? t('users.impersonation.revoke')
                : t('users.impersonation.grant')}
            </button>
          ) : null}
          <button type="button" onClick={onToggle}>
            {user.active ? t('users.deactivate') : t('users.reactivate')}
          </button>
        </span>
      </td>
    </tr>
  );
}

/** A szerepkör felirata — a `common` névtérből, amit a teljes termék használ. */
function roleLabel(t: Translate, user: UserRow): string {
  switch (user.role) {
    case 'ADMIN':
      return t('user.role.ADMIN');
    case 'ACCOUNTANT':
      return t('user.role.ACCOUNTANT');
    case 'CLIENT':
      return t('user.role.CLIENT');
  }
}

/**
 * „MIT LÁT" — a hatókör, a felhasználó szemszögéből.
 *
 * Az ügyfélnél a CÉG NEVE áll, nem a „saját cége" felirat: a lista olvasója
 * azt keresi, KINEK a kapcsolattartója.
 */
function scopeLabel(t: Translate, user: UserRow, companies: CompanyRow[]): string {
  switch (user.scope) {
    case 'ALL_COMPANIES':
      return t('user.scope.ALL_COMPANIES');
    case 'ASSIGNED':
      return t('users.assignedCount', { count: user.companyIds.length });
    case 'OWN_COMPANY':
      return (
        companies.find((company) => company.id === user.companyId)?.name ??
        t('user.scope.OWN_COMPANY')
      );
  }
}

/** A megerősítés a KÖVETKEZMÉNYT mondja el, nem azt, hogy „biztos vagy benne". */
function confirmMessage(t: Translate, confirming: NonNullable<Confirming>): string {
  const name = confirming.user.name;
  if (confirming.kind === 'active') {
    return confirming.active
      ? t('users.reactivateConfirm', { name })
      : t('users.deactivateConfirm', { name });
  }
  return confirming.canImpersonate
    ? t('users.impersonation.grantConfirm', { name })
    : t('users.impersonation.revokeConfirm', { name });
}

function stateLabel(t: Translate, user: UserRow): string {
  if (!user.active) {
    return t('users.inactive');
  }
  return user.lastLoginAt === null ? t('users.pending') : t('users.active');
}

/**
 * A MEGHÍVÓ SORSA — a felvétel és az újraküldés után.
 *
 * ⚠️ A `failed` HIBAKÉNT JELENIK MEG, DE NEM A FELVÉTEL HIBÁJAKÉNT: a
 * felhasználó a listában áll, és a sorában ott a „Meghívó újraküldése".
 */
function InvitationNoticeView({ state }: { state: UsersState }) {
  const { t } = useTranslation();

  if (!state.invitation) {
    return null;
  }

  const { outcome, email } = state.invitation;
  const dismiss = (
    <button type="button" onClick={state.dismissInvitation}>
      {t('users.dismiss')}
    </button>
  );

  return outcome === 'sent' ? (
    <output className="notice">
      {t('users.invitationSent', { email })} {dismiss}
    </output>
  ) : (
    <p className="error" role="alert">
      {t('users.invitationFailed', { email })} {dismiss}
    </p>
  );
}

/**
 * A SOR-MŰVELETEK HIBÁJA — inaktiválás, újraküldés.
 *
 * ⚠️ A 403 (`refused`) ÉS A 409 A SZERVER MONDATÁT MUTATJA, SZÓ SZERINT: „a
 * saját fiókodat nem inaktiválhatod", „már beállította a jelszavát". Egy
 * saját „nem sikerült" ezeknél elnyelné a teendőt.
 */
function WriteError({ state }: { state: UsersState }) {
  const { t } = useTranslation();

  if (state.status === 'failed') {
    return (
      <p className="error" role="alert">
        {t('users.stateFailed')}
      </p>
    );
  }
  if (state.status === 'refused' || state.status === 'conflict' || state.status === 'invalid') {
    return (
      <p className="error" role="alert">
        {state.detail}
      </p>
    );
  }
  return null;
}

/** A három ág, ami megállítja a lapot — betöltés, tiltás, hibás betöltés. */
function blockingView(state: UsersState, t: Translate): ReactElement | null {
  if (state.status === 'forbidden') {
    return (
      <p className="error" role="alert">
        {t('users.forbidden')}
      </p>
    );
  }

  if (state.status === 'loading' && state.users.length === 0) {
    return <p className="subtitle">{t('users.loading')}</p>;
  }

  if (state.status === 'loadFailed') {
    return (
      <p className="error" role="alert">
        {t('users.loadFailed')}{' '}
        <button type="button" onClick={state.reload}>
          {t('users.retry')}
        </button>
      </p>
    );
  }

  return null;
}
